import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LangPicker } from "../LangPicker";
import { Button } from "../ui/button";
import RichTextEditor from "../RichTextEditor";
import { Save, Sparkles, Pencil, Users, Loader2 } from "lucide-react";
import { PartnerSwitch } from "./analyses/AnalysisTab";
import { API } from "../../App";

export const NOTE_FIELDS = {
  summary: { label: "Résumé de séance", hint: "Thème principal, éléments cliniques saillants, interventions, prises de conscience." },
  homework: { label: "Travail personnel convenu", hint: "Exercices, auto-observations, expériences comportementales négociés avec le patient." },
  next_plan: { label: "Plan pour la prochaine séance", hint: "Revue du travail personnel, thèmes à reprendre, techniques envisagées." },
};

export const noteOf = (report, field, target) => (target ? report?.notes_by_patient?.[target]?.[field] : report?.[field]) || "";

const applyNotes = (report, res, target) => {
  const notes = { summary: res.summary, homework: res.homework, next_plan: res.next_plan };
  if (!target) return { ...report, ...notes };
  return { ...report, notes_by_patient: { ...(report.notes_by_patient || {}), [target]: { ...(report.notes_by_patient?.[target] || {}), ...notes, updated_at: res.updated_at } } };
};

export const SessionNoteTab = ({ report, patient, field, onUpdated, participants = [], target = "", onTargetChange }) => {
  const couple = participants.length > 1;
  const tgt = couple ? target : "";
  const stored = noteOf(report, field, tgt);
  const [value, setValue] = useState(stored);
  const [lang, setLang] = useState("");
  const [editing, setEditing] = useState(!stored);
  const [busy, setBusy] = useState("");
  const meta = NOTE_FIELDS[field];
  const who = couple ? participants.find((p) => p.user_id === tgt) : null;
  const hasSource = !!(report.transcript || "").trim() || Object.values(report.fields || {}).some(Boolean);

  useEffect(() => { setValue(stored); setEditing(!stored); }, [report.report_id, stored, field, tgt]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setBusy("save");
    try {
      const res = await axios.put(`${API}/session-reports/${report.report_id}`, { [field]: value, ...(tgt ? { patient_id: tgt } : {}) }, { withCredentials: true });
      onUpdated(res.data); setEditing(false); toast.success(`${meta.label} enregistré${who ? ` pour ${who.first_name}` : ""}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(""); }
  };

  const generateFor = async (pid) => {
    const res = await axios.post(`${API}/session-reports/${report.report_id}/generate-summary`, {}, { withCredentials: true, params: { ...(lang ? { lang } : {}), ...(pid ? { patient_id: pid } : {}) } });
    return res.data;
  };

  const generate = async () => {
    setBusy("ai");
    try {
      const res = await generateFor(tgt);
      onUpdated(applyNotes(report, res, tgt));
      toast.success(`Résumé, travail personnel et plan générés${who ? ` pour ${who.first_name}` : ""} · ${res.route?.label || res.model}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur de génération"); }
    finally { setBusy(""); }
  };

  const generateBoth = async () => {
    setBusy("both");
    let current = report;
    for (const p of participants) {
      try { current = applyNotes(current, await generateFor(p.user_id), p.user_id); onUpdated(current); toast.success(`Notes générées pour ${p.first_name}`); }
      catch (e) { toast.error(`${p.first_name} : ${e.response?.data?.detail || "Erreur de génération"}`); }
    }
    setBusy("");
  };

  return (
    <div className="space-y-3" data-testid={`session-note-${field}`}>
      {couple && (
        <div className="flex items-center gap-3 flex-wrap rounded-lg border border-rose-200 dark:border-rose-900/50 bg-rose-50/40 dark:bg-rose-950/10 px-3 py-2" data-testid="note-couple-bar">
          <PartnerSwitch participants={participants} target={tgt} onChange={onTargetChange} disabled={busy !== ""} />
          <p className="text-xs text-rose-900/80 dark:text-rose-200/80 flex-1 min-w-[200px]" data-testid="note-couple-hint">
            {who ? <>Notes propres à <strong>{who.first_name}</strong> (son vécu, ses exercices, les thèmes à reprendre avec lui/elle).</> : <>Notes <strong>communes</strong> à la séance de couple.</>}
          </p>
          <Button variant="outline" size="sm" onClick={generateBoth} disabled={busy !== "" || !hasSource} className="gap-1.5 border-rose-300 text-rose-800 dark:text-rose-200" data-testid={`note-generate-both-${field}`}>
            {busy === "both" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Users className="w-3.5 h-3.5" />} {busy === "both" ? "Génération…" : `Générer pour ${participants.map((p) => p.first_name).join(" et ")}`}
          </Button>
        </div>
      )}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <p className="text-xs text-muted-foreground max-w-xl">{meta.hint}</p>
        <div className="flex gap-2">
          <LangPicker value={lang} onChange={setLang} patientLang={who ? who.language : patient?.language} testId={`note-lang-${field}`} />
          <Button variant="outline" size="sm" onClick={generate} disabled={busy !== "" || !hasSource} className="gap-1.5" title={hasSource ? "Génère les 3 notes (résumé, travail personnel, plan) à partir de la transcription" : "Ajoutez d'abord une transcription ou un compte rendu"} data-testid={`note-generate-${field}`}>
            <Sparkles className="w-3.5 h-3.5" /> {busy === "ai" ? "Génération..." : "Générer avec l'IA (les 3 notes)"}
          </Button>
          {!editing && <Button variant="ghost" size="sm" onClick={() => setEditing(true)} className="gap-1.5" data-testid={`note-edit-${field}`}><Pencil className="w-3.5 h-3.5" /> Modifier</Button>}
        </div>
      </div>
      {editing ? (
        <>
          <RichTextEditor value={value} onChange={setValue} placeholder={`${meta.label}…`} />
          <div className="flex justify-end gap-2">
            {stored && <Button variant="ghost" size="sm" onClick={() => { setValue(stored); setEditing(false); }}>Annuler</Button>}
            <Button size="sm" onClick={save} disabled={busy !== "" || value === stored} className="gap-1.5" data-testid={`note-save-${field}`}><Save className="w-3.5 h-3.5" /> {busy === "save" ? "Enregistrement..." : "Enregistrer"}</Button>
          </div>
        </>
      ) : (
        <div className="prose prose-sm dark:prose-invert max-w-none text-sm bg-muted/30 rounded-lg p-3" dangerouslySetInnerHTML={{ __html: value }} data-testid={`note-content-${field}`} />
      )}
    </div>
  );
};
